import { ReactNode } from 'react';
import { Flex } from '@chakra-ui/react';

/**
 * A generic sub-header bar below the primary Navbar that carries page-specific
 * controls, so the primary Navbar can stay identical everywhere. It knows
 * nothing about editors; editor toolbars are composed on top of it (see
 * EditorToolbar).
 *
 * Two zones laid out with flex-wrap. On a wide viewport `left` grows to fill the
 * bar and `right` sits at its natural width on the end. On a narrow viewport
 * `left` claims a full-width row and `right` wraps onto a second row beneath it,
 * so the bar grows taller rather than crushing its content. Keeps a one-row
 * minimum height.
 */
export function SecondaryHeader({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <Flex
      as="section"
      flexWrap="wrap"
      alignItems="center"
      rowGap="2"
      columnGap="4"
      // Fixed height once it fits on one row (md+), sized to comfortably hold a
      // button, so the bar is identical on every page whatever the right zone
      // holds (pill, button, icon, or nothing). At that fixed height, centring
      // alone owns the vertical rhythm, so drop the padding that would otherwise
      // fight it and skew the top/bottom gap. On narrow screens the height goes
      // auto and the padding returns, giving the wrapped second row breathing room.
      h={{ base: 'auto', md: 12 }}
      minH={12}
      py={{ base: '2', md: '0' }}
      flexShrink={0}
      borderBottomWidth="1px"
      bg="bg.subtle"
      px="4"
    >
      {/* Grows to fill the bar so the breadcrumb/title can use the spare space;
          full-width on narrow so it takes its own row and pushes `right` onto a
          second row beneath it. */}
      <Flex flex={{ base: '1 1 100%', md: 1 }} minW={0} alignItems="center">
        {left}
      </Flex>
      {right ? (
        <Flex
          flex={{ base: '1 1 auto', md: '0 0 auto' }}
          minW={0}
          alignItems="center"
          justifyContent="flex-end"
        >
          {right}
        </Flex>
      ) : null}
    </Flex>
  );
}
